"use client"

// Left-column input card for the Lawn Lime Calculator. Fully controlled: it holds
// no result state, just raises input changes to the island, which computes the
// live result via lib/lime-calculator. Layout/copy mirror the approved design
// mockup (Lawn Lime Calculator.dc.html); numbers/targets come from the engine.

import { MapPin, ChevronDown, ChevronRight } from "lucide-react"
import { GRASS_AGRONOMY } from "@/lib/soil-test/agronomy-tables"
import { GRASS_KEYS, type GrassKey } from "@/lib/soil-test/types"
import type { MagnesiumLevel } from "@/lib/lime-calculator/calculator"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"

export type LimeSoilTexture = "sandy" | "loam" | "clay"

export interface LimeFormState {
  currentPh: number
  grassType: GrassKey
  soilTexture: LimeSoilTexture
  lawnSizeSqft: number
  magnesium: MagnesiumLevel
  zipCode: string
  advancedOpen: boolean
}

export const DEFAULT_LIME_FORM: LimeFormState = {
  currentPh: 6.0,
  grassType: "tall-fescue",
  soilTexture: "loam",
  lawnSizeSqft: 5000,
  magnesium: "unknown",
  zipCode: "",
  advancedOpen: false,
}

const SOIL_TILES: { id: LimeSoilTexture; label: string; sub: string }[] = [
  { id: "sandy", label: "Sandy", sub: "Gritty, drains fast" },
  { id: "loam", label: "Loam", sub: "Crumbly, holds shape briefly" },
  { id: "clay", label: "Clay", sub: "Sticky, ribbons when wet" },
]

const SIZE_PRESETS = [2500, 5000, 10000, 20000]

const MG_OPTIONS: { id: MagnesiumLevel; label: string }[] = [
  { id: "adequate", label: "Adequate" },
  { id: "low", label: "Low" },
  { id: "unknown", label: "Don't know" },
]

function phWord(ph: number): string {
  if (ph < 5.5) return "Very acidic"
  if (ph < 6.5) return "Acidic"
  if (ph <= 7.2) return "Near neutral"
  if (ph <= 8) return "Alkaline"
  return "Very alkaline"
}

const stepLabel = "mb-3 text-[13px] font-bold uppercase tracking-[0.05em] text-green-700"
const chipBase =
  "inline-flex items-center gap-1.5 min-h-10 px-4 rounded-full cursor-pointer text-sm font-bold transition-colors"

interface Props {
  value: LimeFormState
  onChange: (next: LimeFormState) => void
  /** Regional soil hint from the engine (ZIP-derived), shown in the advanced panel. */
  regionHint: string | null
}

export function LimeCalculatorForm({ value, onChange, regionHint }: Props) {
  const set = (patch: Partial<LimeFormState>) => onChange({ ...value, ...patch })
  const inRangeWord = phWord(value.currentPh)

  return (
    <div className="rounded-3xl bg-white border border-gray-200 p-6 sm:p-7 shadow-[0_12px_40px_rgba(0,0,0,0.04)]">
      {/* 1 — current pH */}
      <p className={stepLabel}>1 · Your current soil pH</p>
      <div className="flex items-center gap-4">
        <div className="shrink-0 px-4 py-3 rounded-2xl bg-green-50 border border-green-200 text-center">
          <div className="text-[28px] font-extrabold leading-none text-green-900">
            {value.currentPh.toFixed(1)}
          </div>
          <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.04em] text-amber-600">
            {inRangeWord}
          </div>
        </div>
        <div className="flex-1">
          <input
            type="range"
            min={4}
            max={9}
            step={0.1}
            value={value.currentPh}
            onChange={(e) => set({ currentPh: parseFloat(e.target.value) })}
            className="w-full accent-green-600"
            aria-label="Current soil pH"
          />
          <div className="flex justify-between mt-0.5">
            <span className="text-[11px] font-semibold text-gray-500">4.0 acidic</span>
            <span className="text-[11px] font-semibold text-gray-500">7.0 neutral</span>
            <span className="text-[11px] font-semibold text-gray-500">9.0 alkaline</span>
          </div>
        </div>
      </div>
      <p className="mt-2.5 text-[13px] text-gray-500">
        From a $10 pH strip or probe. Have a full lab report?{" "}
        <a href="/tools/soil-test-analyzer" className="font-semibold text-green-600 underline underline-offset-2">
          Use the Soil Test Analyzer
        </a>{" "}
        instead.
      </p>

      {/* 2 — grass type */}
      <p className={`${stepLabel} mt-6`}>
        2 · Grass type{" "}
        <span className="font-semibold normal-case tracking-normal text-gray-500">(sets your target pH)</span>
      </p>
      <div className="flex flex-wrap gap-2">
        {GRASS_KEYS.map((key) => {
          const g = GRASS_AGRONOMY[key]
          const selected = key === value.grassType
          return (
            <button
              key={key}
              type="button"
              onClick={() => set({ grassType: key })}
              aria-pressed={selected}
              className={`${chipBase} ${
                selected
                  ? "border-2 border-green-600 bg-green-50 text-green-800"
                  : "border border-gray-200 bg-white text-gray-700 hover:border-green-300"
              }`}
            >
              {g.displayName}
              <span className="text-[11.5px] font-bold text-gray-400">pH {g.ph.ideal.toFixed(1)}</span>
            </button>
          )
        })}
      </div>

      {/* 3 — soil texture */}
      <p className={`${stepLabel} mt-6`}>3 · Soil texture</p>
      <div className="grid grid-cols-3 gap-2.5">
        {SOIL_TILES.map((s) => {
          const selected = s.id === value.soilTexture
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => set({ soilTexture: s.id })}
              aria-pressed={selected}
              className={`flex flex-col items-center gap-2 px-2 py-3.5 rounded-2xl cursor-pointer text-center transition-transform hover:-translate-y-0.5 ${
                selected ? "border-2 border-green-600 bg-green-50" : "border border-gray-200 bg-white"
              }`}
            >
              <span className="text-[13px] font-bold text-gray-900">{s.label}</span>
              <span className="text-[11px] leading-snug text-gray-500">{s.sub}</span>
            </button>
          )
        })}
      </div>

      {/* 4 — lawn size */}
      <p className={`${stepLabel} mt-6`}>4 · Lawn size</p>
      <div className="relative">
        <input
          type="number"
          inputMode="numeric"
          min={0}
          value={value.lawnSizeSqft || ""}
          onChange={(e) => set({ lawnSizeSqft: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
          className="w-full min-h-[52px] pl-4 pr-[74px] text-[17px] font-bold text-gray-900 border border-gray-200 rounded-xl bg-white outline-hidden focus:border-green-600 focus:ring-4 focus:ring-green-200/60"
          aria-label="Lawn size in square feet"
        />
        <span className="absolute top-1/2 right-4 -translate-y-1/2 text-[13px] font-semibold text-gray-500">
          sq ft
        </span>
      </div>
      <div className="flex flex-wrap gap-2 mt-2.5">
        {SIZE_PRESETS.map((v) => {
          const selected = v === value.lawnSizeSqft
          return (
            <button
              key={v}
              type="button"
              onClick={() => set({ lawnSizeSqft: v })}
              aria-pressed={selected}
              className={`inline-flex items-center min-h-9 px-3.5 rounded-full cursor-pointer text-[13px] font-bold text-gray-900 transition-colors ${
                selected ? "border-2 border-green-600 bg-green-50" : "border border-gray-200 bg-white hover:border-green-300"
              }`}
            >
              {v.toLocaleString()} sq ft
            </button>
          )
        })}
      </div>

      {/* Advanced */}
      <button
        type="button"
        onClick={() => set({ advancedOpen: !value.advancedOpen })}
        aria-expanded={value.advancedOpen}
        className="flex items-center gap-2 w-full min-h-[44px] mt-5 px-1 border-t border-gray-100 text-[13.5px] font-bold text-gray-700 text-left"
      >
        {value.advancedOpen ? (
          <ChevronDown className="w-4 h-4 text-green-600" />
        ) : (
          <ChevronRight className="w-4 h-4 text-green-600" />
        )}
        Advanced: magnesium level &amp; regional hint
      </button>

      {value.advancedOpen && (
        <div className="flex flex-col gap-4 pt-1">
          <div>
            <p className="mb-2 text-[12.5px] font-bold text-gray-500">
              Magnesium level (from a soil test, if you have one)
            </p>
            <div className="flex flex-wrap gap-2">
              {MG_OPTIONS.map((m) => {
                const selected = m.id === value.magnesium
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => set({ magnesium: m.id })}
                    aria-pressed={selected}
                    className={`inline-flex items-center min-h-9 px-3.5 rounded-full cursor-pointer text-[13px] font-bold text-gray-900 transition-colors ${
                      selected ? "border-2 border-green-600 bg-green-50" : "border border-gray-200 bg-white hover:border-green-300"
                    }`}
                  >
                    {m.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <p className="mb-2 text-[12.5px] font-bold text-gray-500">
              ZIP code (regional soil hint, doesn't change the math)
            </p>
            <div className="relative max-w-[260px]">
              <MapPin className="absolute top-1/2 left-3.5 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                {...ZIP_INPUT_ATTRS}
                value={value.zipCode}
                onChange={(e) => set({ zipCode: sanitizeZip(e.target.value) })}
                placeholder="e.g. 28202"
                className="w-full min-h-[44px] pl-9.5 pr-3.5 text-[15px] font-bold tracking-[0.04em] text-gray-900 border border-gray-200 rounded-xl bg-white outline-hidden focus:border-green-600 focus:ring-4 focus:ring-green-200/60"
                aria-label="ZIP code"
              />
            </div>
            {regionHint && (
              <p className="mt-2 text-[13px] leading-normal text-gray-600">{regionHint}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
